import { Flex } from 'antd';
import { PrimaryCard } from '../../assets/styles/components/dashboard.styled';
import { Button, Divider, IconButton, Tooltip } from '../Ant';
import CardData from '../dashboard/cardData';
import {
  ArrowRight,
  Briefcase,
  Calendar,
  CalendarDate,
  Crown,
  Dashboard,
  DollarCircle,
  LanguageFilled,
  Receipt,
  ShieldTick,
  Star,
  StarFilled,
  User,
  Verify,
} from '../icons';
import { useTranslation } from 'react-i18next';
import { Courthouse } from '../icons/Courthouse';
import { IMatter } from '../../interface/matter';
import {
  getTranslationFromTranslatableEntity,
  matterBillingStatusTranslationMapping,
  MatterStatus,
} from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { InvoiceableType } from '../../constants/transaction';
import {
  displayLanguageName,
  getLocalDateAndTime,
  getMatterIconClass,
  getMatterLabel,
} from '../../constants/utilityFunctions';
import { useCreateFavorite } from '../../hooks/useCreateFavorite';
import { useRemoveFromFavorite } from '../../hooks/useRemoveFromFavorite';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import { BorderColor, TagColor } from '../../interface/common';
import NoteIcons from '../common/NoteIcons';
import { MatterIndicators } from '../matters/matterIndicators';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import { getViewLabel } from '../../helpers/viewLabelHelper';

const MatterGeneralCard = ({
  matter,
  refetch,
  isLoading = false,
}: {
  matter: IMatter;
  refetch: () => void;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const { getAppLanguageCode } = useLanguage();
  const { push } = useNavigationStack();
  const isFavorite = matter?.isFavorite ?? false;
  const createFavoriteMutation = useCreateFavorite();
  const removeMatterFavoriteMutation = useRemoveFromFavorite();
  const handleToggle = () => {
    if (!isFavorite) {
      createFavoriteMutation.mutate(
        {
          matterId: matter?.id,
          successMessage: t('matter:addedToYourFavorites'),
        },
        {
          onSuccess: () => {
            refetch();
          },
        },
      );
    } else {
      removeMatterFavoriteMutation.mutate(
        {
          matterId: matter?.id,
          successMessage: t('matter:removedFromYourFavorites'),
        },
        {
          onSuccess: () => {
            refetch();
          },
        },
      );
    }
  };
  const handleGoToCustomer = () => {
    push(
      ROUTES.CUSTOMER_LISTING +
        `/${matter?.principalCompany?.id ?? matter?.principalPrivatePerson?.id}`,
    );
  };
  if (isLoading || !matter) {
    return (
      <SkeletonEntityCard
        rows={[1, 1, 1, 1, 1, 2, 2, 2]}
        withSubcontentOnFirstRow
        className="py-10 px-10 h-full"
      />
    );
  }
  return (
    <PrimaryCard
      data-testid="matter-general-card"
      $isDetail
      className={`d-flex flex-col gap-10 h-full customer-card ${matter.status === MatterStatus.ACTIVE ? BorderColor.GREEN : BorderColor.RED}`}
    >
      <Flex justify="space-between" align="center" className="w-full">
        <Flex gap={10} align="center">
          <Flex
            data-testid="matter-general-card-status-chip"
            justify="center"
            align="center"
            className={`status-chip ${matter.status === MatterStatus.ACTIVE ? TagColor.GREEN : TagColor.RED}`}
          >
            {t('matter:status.' + matter?.status)}
          </Flex>
          <NoteIcons
            hasImportantNotes={matter?.hasImportantNotes}
            hasImportantRelatedNotes={matter?.hasImportantRelatedNotes}
            hasNotes={matter?.hasNotes}
          />
          <MatterIndicators
            invoiceableType={matter?.invoiceableType as InvoiceableType}
            hasSubMatters={Boolean(matter?.hasSubMatters)}
            parentMatterId={matter?.parentMatterId}
            fixedPriceInUse={matter?.fixedPriceInUse}
            insider={matter?.insider}
            fixedPrice={matter?.fixedPrice}
            currency={matter?.currency}
          />
        </Flex>
        <Tooltip title={t('common:favorite')} placement="top" arrow={false}>
          <IconButton
            aria-label={t('common:favorite')}
            aria-pressed={Boolean(isFavorite)}
            className={`favourite-icon ${isFavorite ? 'active' : ''}`}
            onClick={handleToggle}
          >
            {isFavorite ? <StarFilled /> : <Star />}
          </IconButton>
        </Tooltip>
      </Flex>
      <Divider />
      <Flex vertical gap={10} className="flex-grow">
        <CardData
          type="matter"
          icon={<Briefcase className={getMatterIconClass(matter?.invoiceableType ?? '')} />}
          title={getMatterLabel(t, matter?.invoiceableType ?? '')}
          content={
            matter?.principalCompany?.name ?? matter?.principalPrivatePerson?.fullName ?? '-'
          }
          subcontent={matter?.matterNumber && `${matter.matterNumber} - ${matter.subject}`}
          contentclass="lineclamp-1"
          subcontentclass="lineclamp-1"
          headingcontent={
            <Button
              data-testid="matter-general-card-view-customer"
              type="text"
              icon={<ArrowRight />}
              iconPosition="end"
              className="text-cta"
              onClick={handleGoToCustomer}
            >
              {getViewLabel(t, 'customer:customer')}
            </Button>
          }
        />

        <Flex justify="space-between">
          <CardData
            icon={<ShieldTick />}
            type="responsiblePerson"
            title={t('matter:responsiblePerson')}
            content={matter?.responsibleUser?.fullName ?? '-'}
          />
          <CardData
            icon={<User />}
            type="owner"
            title={t('matter:owner')}
            content={matter?.ownerUser?.fullName ?? '-'}
          />
        </Flex>
        <Divider />
        <CardData
          icon={<Dashboard />}
          type="matterType"
          title={t('matter:type')}
          content={getTranslationFromTranslatableEntity(
            matter?.matterType?.translations,
            getAppLanguageCode(),
          )}
        />
        <CardData
          icon={<Crown />}
          type="businessArea"
          title={t('matter:businessArea')}
          content={getTranslationFromTranslatableEntity(
            matter?.businessArea?.translations,
            getAppLanguageCode(),
          )}
        />
        <Divider />
        <Flex justify="space-between">
          <CardData
            type="billing"
            icon={<Receipt />}
            title={t('matter:billing')}
            content={t(
              matterBillingStatusTranslationMapping[
                (matter?.invoiceableType as keyof typeof matterBillingStatusTranslationMapping) ??
                  ''
              ],
            )}
          />
          <CardData
            icon={<Calendar />}
            type="openingDate"
            title={t('matter:openingDate')}
            content={getLocalDateAndTime(new Date(matter?.openingDate ?? ''), true)}
          />
        </Flex>
        <Flex justify="space-between">
          {matter.invoiceableType === InvoiceableType.INTERNAL ? (
            <div className="w-full" />
          ) : (
            <CardData
              type="currency"
              icon={<DollarCircle />}
              title={t('matter:currency')}
              content={`${matter?.currency ?? '-'}`}
            />
          )}
          <CardData
            type="language"
            icon={<LanguageFilled />}
            title={t('common:language')}
            content={
              matter?.language ? displayLanguageName(matter?.language, getAppLanguageCode()) : '-'
            }
          />
        </Flex>
        <Flex justify="space-between">
          <CardData
            type="conflictCheckDoneDate"
            icon={<Verify />}
            title={t('matter:conflictCheckDoneDate')}
            content={
              matter?.conflictCheckDoneDate
                ? getLocalDateAndTime(new Date(matter?.conflictCheckDoneDate), true)
                : '-'
            }
          />
          <CardData
            icon={<CalendarDate />}
            title={t('matter:lastEntryDate')}
            content={
              matter?.lastEntryDate
                ? getLocalDateAndTime(new Date(matter?.lastEntryDate), true)
                : '-'
            }
          />
        </Flex>
        <Divider />
        <CardData
          icon={<Courthouse />}
          type="businessUnit"
          title={t('matter:businessUnit')}
          content={matter?.businessUnit?.name}
        />
      </Flex>
    </PrimaryCard>
  );
};

export default MatterGeneralCard;
